/**
 * Reel Generation Page
 * Feature 017: Instagram Reel Creation
 * Main page for research + multi-model reel script generation
 */

import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAction, useQuery } from 'convex/react';
import { Id } from '../../convex/_generated/dataModel';
import { api } from '../../convex/_generated/api';
import { useReelGenerationMultiAI } from '../hooks/useReelGeneration';
import { useBusinessProfile } from '../hooks/useBusinessProfile';
import { ReelGenerationSkeletonMemo } from '../components/reel-generation/ReelGenerationSkeleton';
import { MultiModelReelDisplayMemo } from '../components/reel-generation/MultiModelReelDisplay';
import { ArrowLeft, Film } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '../components/ui/dialog';

const ReelGenerationPage = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get reelId from URL params if provided, otherwise we'll create one
  const reelIdFromUrl = searchParams.get('reelId');

  // Get business profile for reel creation
  const { profile } = useBusinessProfile();

  // Create reel action
  const createReelForTopicAction = useAction(api.ai.reelGeneration.createReelForTopic);

  // Integrate hooks - initially undefined until reel is created/loaded
  const { state, handleStartGeneration, handleSelectBestVariant } = useReelGenerationMultiAI(
    reelIdFromUrl as Id<'reels'> | undefined,
    topicId as Id<'topics'>
  );

  // Fetch topic data to display topic name
  const topic = useQuery(
    api.topics.getTopicById,
    topicId ? { topicId: topicId as Id<'topics'> } : 'skip'
  );

  // Dialog state for showing research results
  const [isResearchDialogOpen, setIsResearchDialogOpen] = useState(false);

  // Ref to prevent duplicate generation calls
  const hasStartedGeneration = useRef(false);

  // Auto-trigger reel creation and research on page load
  useEffect(() => {
    if (!topicId || !profile) return;

    const initializeGeneration = async () => {
      try {
        // If we don't have a reelId yet, create one
        if (!reelIdFromUrl) {
          const newReelId = await createReelForTopicAction({
            topicId: topicId as Id<'topics'>,
            businessId: profile._id,
          });

          // Update URL with new reelId
          navigate(`/reel-generation/${topicId}?reelId=${newReelId}`, {
            replace: true,
          });
          return;
        }

        // If we have a reelId, we're in idle phase, models are loaded, and haven't started yet
        if (
          reelIdFromUrl &&
          state.phase === 'idle' &&
          state.selectedModels.length > 0 &&
          !hasStartedGeneration.current
        ) {
          hasStartedGeneration.current = true;
          void handleStartGeneration();
        }
      } catch {
        // Error will be shown in UI via state
      }
    };

    void initializeGeneration();
  }, [
    topicId,
    profile,
    reelIdFromUrl,
    state.phase,
    state.selectedModels.length,
    handleStartGeneration,
    createReelForTopicAction,
    navigate,
  ]);

  // Handle back navigation
  const handleBack = () => {
    navigate(-1);
  };

  // Loading state while waiting for profile, topic, or creating reel
  const isInitializing = !profile || !topic || (!reelIdFromUrl && state.phase === 'idle');

  if (isInitializing) {
    return (
      <div className="container mx-auto max-w-7xl px-4 py-8">
        <div className="h-8 w-48 animate-pulse rounded bg-gray-200" />
        <div className="mt-8 h-64 animate-pulse rounded-lg bg-gray-100" />
      </div>
    );
  }

  // Topic not found
  if (!isInitializing && topic === null) {
    return (
      <div className="container mx-auto max-w-7xl px-4 py-8">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <h3 className="font-semibold text-red-900">Topic Not Found</h3>
          <p className="mt-2 text-sm text-red-700">The requested topic could not be found.</p>
          <Button onClick={handleBack} className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-7xl px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={handleBack}
          className="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <div className="flex items-center gap-3">
          <Film className="h-8 w-8 text-purple-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {topic ? topic.name : 'Loading...'}
            </h1>
            <p className="mt-1 text-gray-600">Instagram Reel Script Generation</p>
          </div>
        </div>
        <p className="mt-4 text-gray-600">
          Comparing reel scripts from {state.selectedModels.length} AI model
          {state.selectedModels.length > 1 ? 's' : ''}:{' '}
          {state.selectedModels
            .map(m => (m === 'claude' ? 'Claude' : m === 'gemini' ? 'Gemini' : 'GPT'))
            .join(', ')}
        </p>
      </div>

      {/* Research Phase */}
      <div className="mb-8">
        <h2 className="mb-4 text-xl font-semibold text-gray-900">Research</h2>
        {state.phase === 'idle' && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
            <p className="text-gray-600">Starting research...</p>
          </div>
        )}
        {state.researchLoading && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 animate-pulse">
            <div className="h-4 w-32 mb-4 rounded bg-gray-200" />
            <div className="space-y-2">
              <div className="h-4 w-full rounded bg-gray-200" />
              <div className="h-4 w-3/4 rounded bg-gray-200" />
              <div className="h-4 w-5/6 rounded bg-gray-200" />
            </div>
          </div>
        )}
        {state.researchError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-6">
            <h3 className="font-semibold text-red-900">Research Failed</h3>
            <p className="mt-2 text-sm text-red-700">{state.researchError}</p>
            <Button onClick={handleStartGeneration} className="mt-4">
              Retry
            </Button>
          </div>
        )}
        {state.researchResults && (
          <Button
            variant="outline"
            onClick={() => setIsResearchDialogOpen(true)}
            className="w-full sm:w-auto"
          >
            Show research
          </Button>
        )}
      </div>

      {/* Reel Generation Phase */}
      {(state.phase === 'generation' || state.phase === 'completed') && (
        <div>
          <h2 className="mb-4 text-xl font-semibold text-gray-900">Choose best reel script</h2>

          {/* Show skeleton if all models are still idle */}
          {state.selectedModels.every(m => state.generationStatus[m] === 'idle') && (
            <ReelGenerationSkeletonMemo modelCount={state.selectedModels.length} />
          )}

          {/* Show multi-model display once at least one model starts */}
          {state.selectedModels.some(m => state.generationStatus[m] !== 'idle') && (
            <MultiModelReelDisplayMemo
              selectedModels={state.selectedModels}
              generatedReels={state.generatedReels}
              generationStatus={state.generationStatus}
              selectedVariantModel={state.selectedVariantModel}
              isSelecting={state.isSelecting}
              onSelectBestVariant={handleSelectBestVariant}
            />
          )}
        </div>
      )}

      {/* Research Dialog */}
      {state.researchResults && (
        <Dialog open={isResearchDialogOpen} onOpenChange={setIsResearchDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <span>Research Results</span>
                {state.researchCached && (
                  <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                    Cached
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>

            <div className="prose prose-sm max-w-none mt-4">
              <p className="whitespace-pre-wrap text-gray-700">{state.researchResults}</p>
            </div>

            <DialogClose className="absolute right-4 top-4" />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export { ReelGenerationPage };
